import { computedFn } from 'mobx-utils/lib/computedFn';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import getItemRightsManagementRestrictions from 'owa-mail-store/lib/utils/getItemRightsManagementRestrictions';
import doesFolderIdEqualName from 'owa-session-store/lib/utils/doesFolderIdEqualName';
import type Item from 'owa-service/lib/contract/Item';
import type Message from 'owa-service/lib/contract/Message';
import type { ClientItemId } from 'owa-client-ids';
import { MailRibbonControlId } from 'owa-ribbon-ids';
import checkItemReplyForwardDisabled from 'owa-mail-reading-pane-item-actions/lib/utils/checkItemReplyForwardDisabled';

export const getIsPopoutButtonDisabled = (
    controlId: number | string,
    itemId?: ClientItemId
): boolean => {
    switch (controlId) {
        case MailRibbonControlId.AssignPolicy:
            return shouldDisableAssignPolicy(itemId);
        case MailRibbonControlId.DeleteItem:
            return shouldDisableDelete(itemId);
        case MailRibbonControlId.Print:
            return shouldDisablePrint(itemId);
        case MailRibbonControlId.Phishing:
        case MailRibbonControlId.JunkFlyout:
            return shouldDisableJunk(itemId);

        case MailRibbonControlId.Rules:
        case MailRibbonControlId.ReplyWithMeeting:
        case MailRibbonControlId.ReadUnread:
        case MailRibbonControlId.FlagUnflag:
            return isGroupItem(itemId);

        case MailRibbonControlId.Respond:
        case MailRibbonControlId.RespondReply:
        case MailRibbonControlId.RespondReplyAll:
        case MailRibbonControlId.RespondForward:
            return isRespondItemDisabled(controlId, itemId);

        case MailRibbonControlId.ImmersiveReader:
            return false;

        default:
            return true;
    }
};

const isGroupItem = computedFn((itemId: ClientItemId | undefined): boolean => {
    return itemId?.mailboxInfo?.type == 'GroupMailbox';
});

const getItem = computedFn((itemId: ClientItemId | undefined): Item | undefined => {
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (85,13): Type 'ClientItem | undefined' is not assignable to type 'Item'.
    // @ts-expect-error
    return getMailStore().items.get(itemId.Id);
});

const shouldDisableAssignPolicy = computedFn((itemId: ClientItemId | undefined): boolean => {
    if (itemId) {
        if (isGroupItem(itemId)) {
            return true;
        }
        // we want to disable if it's not one of these mailbox types
        return !(
            itemId.mailboxInfo?.type == 'UserMailbox' ||
            itemId.mailboxInfo?.type == 'ArchiveMailbox' ||
            itemId.mailboxInfo?.type == 'SharedMailbox'
        );
    }
    return true;
});

const shouldDisableDelete = computedFn((itemId: ClientItemId | undefined): boolean => {
    if (!isGroupItem(itemId)) {
        return false;
    }
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (85,13): Type 'Item | undefined' is not assignable to type 'Message'.
    // @ts-expect-error
    const message: Message = getItem(itemId);
    return message ? !message.CanDelete : true;
});

const shouldDisableJunk = computedFn((itemId: ClientItemId | undefined): boolean => {
    const item: Item | undefined = getItem(itemId);
    return (
        isGroupItem(itemId) ||
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2345 (97,48): Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        // @ts-expect-error
        (item ? doesFolderIdEqualName(item.ParentFolderId?.Id, 'junkemail') : true)
    );
});

const shouldDisablePrint = computedFn((itemId: ClientItemId | undefined): boolean => {
    const item: Item | undefined = getItem(itemId);
    if (item) {
        const irmRestrictions = getItemRightsManagementRestrictions(item);
        return irmRestrictions ? !irmRestrictions.PrintAllowed : false;
    }
    return true;
});

const isRespondItemDisabled = computedFn(
    (controlId: number | string, itemId: ClientItemId | undefined): boolean => {
        const item: Item | undefined = getItem(itemId);
        if (item) {
            const itemResponseDisabled = checkItemReplyForwardDisabled(
                item,
                false /*isConversationItemPart*/
            );
            switch (controlId) {
                case MailRibbonControlId.Respond:
                    return (
                        itemResponseDisabled[0] &&
                        itemResponseDisabled[1] &&
                        itemResponseDisabled[2]
                    );
                case MailRibbonControlId.RespondReply:
                    return itemResponseDisabled[0];
                case MailRibbonControlId.RespondReplyAll:
                    return itemResponseDisabled[1];
                case MailRibbonControlId.RespondForward:
                    return itemResponseDisabled[2];
                default:
                    return true;
            }
        }
        return true;
    }
);
